import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminAPI } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Users, CreditCard, Calendar, AlertCircle, ShieldX, Trash, Plus, Bell, Clock, Download, Megaphone, Edit, Archive, Activity, Eye, EyeOff, Copy, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { formatDate } from "../lib/utils";
import UserActivityModal from "../components/UserActivityModal";

/** Returns true only if the string is a valid ISO date in YYYY-MM-DD format. */
function isValidISODate(value) {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) return false;
  const d = new Date(value);
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    cancelled: 0,
    pastDue: 0,
    archived: 0,
  });

  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activitySubscriber, setActivitySubscriber] = useState(null);

  const openActivityModal = (sub) => {
    setActivitySubscriber(sub);
    setActivityModalOpen(true);
  };

  useEffect(() => {
    checkAdminAccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminAccess = async () => {
    try {
      const res = await adminAPI.checkAccess();
      setIsAdmin(res.data.is_admin);
      setAccessChecked(true);
      if (res.data.is_admin) {
        fetchSubscribers();
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error("Failed to check admin access:", err);
      setAccessChecked(true);
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const res = await adminAPI.getSubscribers();
      setSubscribers(res.data);
      
      // Calculate stats
      const newStats = {
        total: res.data.filter(s => s.status !== "archived").length,
        active: res.data.filter(s => s.status === "active").length,
        inactive: res.data.filter(s => s.status === "inactive").length,
        cancelled: res.data.filter(s => s.status === "cancelled").length,
        pastDue: res.data.filter(s => s.status === "past_due").length,
        archived: res.data.filter(s => s.status === "archived").length,
      };
      setStats(newStats);
    } catch (err) {
      console.error("Failed to fetch subscribers:", err);
      if (err.response?.status === 403) {
        setIsAdmin(false);
      } else {
        toast.error("Failed to load subscribers");
      }
    } finally {
      setLoading(false);
    }
  };

  // Create subscriber dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginDetails, setLoginDetails] = useState(null);
  const [newSubscriber, setNewSubscriber] = useState({
    name: "",
    email: "",
    password: "",
    paypal_subscription_id: "",
    status: "active",
    plan_amount: 499,
    paid_until: "",
    currency: "PHP",
    role: "user",
  });

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newSubscriber };
      // strip empty strings to avoid sending blanks
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      // validate paid_until date format
      if (payload.paid_until && !isValidISODate(payload.paid_until)) {
        toast.error("Paid Until must be a valid date in YYYY-MM-DD format (e.g. 2030-12-31)");
        return;
      }
      const res = await adminAPI.createSubscriber(payload);
      toast.success("Subscriber created");
      setCreateOpen(false);
      // Show login details if a new user was created
      if (res.data?.plain_password) {
        setLoginDetails({
          name: newSubscriber.name,
          email: newSubscriber.email,
          password: res.data.plain_password,
        });
      }
      setNewSubscriber({ name: "", email: "", password: "", paypal_subscription_id: "", status: "active", plan_amount: 499, paid_until: "", currency: "PHP", role: "user" });
      setShowPassword(false);
      fetchSubscribers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Failed to create subscriber");
    }
  };

  // Edit subscriber dialog state (admin-created subscriptions only)
  const [editOpen, setEditOpen] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [editForm, setEditForm] = useState({
    status: "active",
    plan_amount: 0,
    paid_until: "",
    currency: "PHP",
    paypal_subscription_id: "",
  });

  const openEditSubscriber = (sub) => {
    setEditingSubscriber(sub);
    setEditForm({
      status: sub.status || "active",
      plan_amount: sub.plan_amount ?? 0,
      paid_until: sub.paid_until || "",
      currency: sub.currency || "PHP",
      paypal_subscription_id: sub.paypal_subscription_id || "",
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm };
      // strip empty strings to send null / omit
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      if (payload.paid_until && !isValidISODate(payload.paid_until)) {
        toast.error("Paid Until must be a valid date in YYYY-MM-DD format (e.g. 2030-12-31)");
        return;
      }
      await adminAPI.updateSubscriber(editingSubscriber.id, payload);
      toast.success("Subscription updated");
      setEditOpen(false);
      setEditingSubscriber(null);
      fetchSubscribers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Failed to update subscription");
    }
  };

  // Reminder state
  const [reminderLogsOpen, setReminderLogsOpen] = useState(false);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [trialPreview, setTrialPreview] = useState(null);

  // Announcement state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    message: "",
    type: "info",
    start_datetime: new Date().toISOString().slice(0, 16),
    end_datetime: "",
    dismissible: true,
    audience: "all",
    active: true,
  });

  const fetchAnnouncements = async () => {
    try {
      const res = await adminAPI.listAnnouncements();
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error("Failed to fetch announcements:", err);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAnnouncements();
      adminAPI.previewTrialReminders().then((r) => setTrialPreview(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const openNewAnnouncement = () => {
    setEditingAnnouncement(null);
    setAnnouncementForm({
      title: "",
      message: "",
      type: "info",
      start_datetime: new Date().toISOString().slice(0, 16),
      end_datetime: "",
      dismissible: true,
      audience: "all",
      active: true,
    });
    setAnnouncementDialogOpen(true);
  };

  const openEditAnnouncement = (ann) => {
    setEditingAnnouncement(ann);
    setAnnouncementForm({
      title: ann.title || "",
      message: ann.message || "",
      type: ann.type || "info",
      start_datetime: ann.start_datetime ? ann.start_datetime.slice(0, 16) : "",
      end_datetime: ann.end_datetime ? ann.end_datetime.slice(0, 16) : "",
      dismissible: ann.dismissible !== false,
      audience: ann.audience || "all",
      active: ann.active !== false,
    });
    setAnnouncementDialogOpen(true);
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...announcementForm,
        end_datetime: announcementForm.end_datetime || null,
      };
      if (editingAnnouncement) {
        await adminAPI.updateAnnouncement(editingAnnouncement.id, payload);
        toast.success("Announcement updated");
      } else {
        await adminAPI.createAnnouncement(payload);
        toast.success("Announcement created");
      }
      setAnnouncementDialogOpen(false);
      fetchAnnouncements();
    } catch (err) {
      console.error("Failed to save announcement:", err);
      toast.error("Failed to save announcement");
    }
  };

  const handleToggleAnnouncement = async (ann) => {
    try {
      await adminAPI.updateAnnouncement(ann.id, { active: !ann.active });
      fetchAnnouncements();
      toast.success(ann.active ? "Announcement deactivated" : "Announcement activated");
    } catch (err) {
      toast.error("Failed to update announcement");
    }
  };

  const handleDeleteAnnouncement = async (ann) => {
    if (!window.confirm(`Delete announcement "${ann.title}"?`)) return;
    try {
      await adminAPI.deleteAnnouncement(ann.id);
      fetchAnnouncements();
      toast.success("Announcement deleted");
    } catch (err) {
      toast.error("Failed to delete announcement");
    }
  };

  // Admin export helpers
  const parseBlobError = async (err) => {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        return json.detail || json.message || text;
      } catch {
        return err.message;
      }
    }
    return err.response?.data?.detail || err.message;
  };

  const handleExportUsers = async () => {
    try {
      const response = await adminAPI.exportUsers();
      const today = new Date().toISOString().split("T")[0];
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_export_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("Users exported successfully");
    } catch (err) {
      const msg = await parseBlobError(err);
      toast.error(`Export failed: ${msg}`);
    }
  };


  const handleSendTrialReminders = async () => {
    setSendingReminder("trial");
    try {
      const res = await adminAPI.sendTrialReminders();
      toast.success(`Trial reminders sent to ${res.data.sent} user(s)`);
      // Refresh preview count after send
      adminAPI.previewTrialReminders().then((r) => setTrialPreview(r.data)).catch(() => {});
    } catch (err) {
      console.error("Failed to send trial reminders:", err);
      toast.error("Failed to send trial reminders");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleViewReminderLogs = async () => {
    try {
      const res = await adminAPI.getReminderLogs();
      setReminderLogs(res.data);
      setReminderLogsOpen(true);
    } catch (err) {
      console.error("Failed to fetch reminder logs:", err);
      toast.error("Failed to load reminder logs");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-500/10 text-green-500 border-green-500/20",
      inactive: "bg-gray-500/10 text-gray-500 border-gray-500/20",
      cancelled: "bg-red-500/10 text-red-500 border-red-500/20",
      past_due: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.inactive}>
        {status?.replace("_", " ") || "unknown"}
      </Badge>
    );
  };

  const getFunnelBadge = (value) =>
    value ? (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Yes</Badge>
    ) : (
      <Badge variant="outline" className="bg-gray-50 text-gray-500">No</Badge>
    );

  const getRiskBadge = (tag) => {
    if (tag === "at_risk") return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">🔴 At Risk</Badge>;
    if (tag === "exploring") return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">🟡 Exploring</Badge>;
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">🟢 Active</Badge>;
  };

  const isPaidUntilExpired = (paidUntil) => {
    if (!paidUntil) return true;
    const today = new Date();
    const paidDate = new Date(paidUntil);
    return paidDate < today;
  };

  const formatPlanAmount = (sub) => {
    // Prefer the dynamically computed amount from account age; fall back to stored plan_amount
    const amount = sub.computed_amount ?? sub.plan_amount ?? 0;
    if (amount > 0) {
      const promoLabel = sub.promo_applied ? " (promo)" : "";
      return `₱${amount}${promoLabel}`;
    }
    return sub.status === "inactive" ? "Trial / Free" : "₱0";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Access denied view
  if (accessChecked && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4" data-testid="admin-access-denied">
        <div className="p-4 rounded-full bg-red-500/10">
          <ShieldX className="h-12 w-12 text-red-500" />
        </div>
        <h1 className="text-2xl font-bold">Access Denied</h1>
        <p className="text-muted-foreground text-center max-w-md">
          You don't have permission to access the admin dashboard. This page is restricted to administrators only.
        </p>
      </div>
    );
  }

  // Split subscribers into operational (non-archived) and archived for separate tables
  const activeSubscribers = subscribers.filter(s => s.status !== "archived");
  const archivedSubscribers = subscribers.filter(s => s.status === "archived");

  // Shared row renderer used by both the active and archived subscriber tables.
  // The first column (User) is sticky so it stays visible during horizontal scroll.
  const renderSubscriberRows = (rows) => rows.map((sub) => (
    <TableRow key={sub.id} className="group">
      <TableCell className="sticky left-0 z-10 bg-background group-hover:bg-muted/50 border-r">
        <div>
          <div className="font-medium">{sub.user_name || "N/A"}</div>
          <div className="text-sm text-muted-foreground">{sub.user_email || "N/A"}</div>
        </div>
      </TableCell>
      <TableCell>{sub.business_name || "-"}</TableCell>
      <TableCell>{getStatusBadge(sub.status)}</TableCell>
      <TableCell>{getRiskBadge(sub.risk_tag)}</TableCell>
      <TableCell className="text-sm">
        {sub.user_created_at ? formatDate(sub.user_created_at) : "-"}
      </TableCell>
      <TableCell className="text-sm">
        {sub.last_activity ? formatDate(sub.last_activity) : <span className="text-muted-foreground">None</span>}
      </TableCell>
      <TableCell className="text-center font-medium">
        {sub.total_activity_count ?? 0}
      </TableCell>
      <TableCell>
        {sub.activated ? (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Yes</Badge>
        ) : (
          <Badge variant="outline" className="bg-gray-50 text-gray-500">No</Badge>
        )}
      </TableCell>
      <TableCell>{getFunnelBadge(sub.logged_in)}</TableCell>
      <TableCell>{getFunnelBadge(sub.created_client)}</TableCell>
      <TableCell>{getFunnelBadge(sub.created_time_entry)}</TableCell>
      <TableCell>{getFunnelBadge(sub.created_invoice)}</TableCell>
      <TableCell>{getFunnelBadge(sub.used_financials)}</TableCell>
      <TableCell>{getFunnelBadge(sub.used_budget_planner)}</TableCell>
      <TableCell>
        <span className="text-xs whitespace-nowrap">{sub.drop_off_stage || "Signup Only"}</span>
      </TableCell>
      <TableCell className="font-mono text-xs">
        {sub.paypal_subscription_id ? (
          <span className="truncate block max-w-[150px]" title={sub.paypal_subscription_id}>
            {sub.paypal_subscription_id}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>
        {sub.paid_until ? (
          <span className={isPaidUntilExpired(sub.paid_until) ? "text-red-500" : ""}>
            {formatDate(sub.paid_until)}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>
        {sub.last_payment_date ? formatDate(sub.last_payment_date) : "-"}
      </TableCell>
      <TableCell>
        {formatPlanAmount(sub)}
      </TableCell>
      <TableCell>
        {sub.creation_source === "admin_created"
          ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap">Admin Created</Badge>
          : sub.creation_source === "self_registered"
            ? <Badge variant="outline" className="bg-gray-50 text-gray-500 whitespace-nowrap">Self-Registered</Badge>
            : <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 whitespace-nowrap">Unknown Source</Badge>
        }
      </TableCell>
      <TableCell>
        {sub.user_role === "admin"
          ? <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 whitespace-nowrap">admin</Badge>
          : <Badge variant="outline" className="bg-gray-50 text-gray-500 whitespace-nowrap">user</Badge>
        }
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openActivityModal(sub)}
            title="View Activity"
          >
            <Activity className="h-4 w-4 mr-1" />
            Activity
          </Button>
          {sub.creation_source === "admin_created" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEditSubscriber(sub)}
              title="Edit Subscription"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          ) : (
            <span
              className="text-xs text-muted-foreground px-2"
              title="Only admin-created subscriptions can be edited"
            >
              Read-only
            </span>
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              if (!window.confirm(`Delete subscriber ${sub.user_email || sub.user_name || sub.id}? This cannot be undone.`)) return;
              try {
                await adminAPI.deleteSubscriber(sub.id);
                // remove locally
                const newList = subscribers.filter(s => s.id !== sub.id);
                setSubscribers(newList);
                // recalc stats
                const newStats = {
                  total: newList.filter(s => s.status !== "archived").length,
                  active: newList.filter(s => s.status === "active").length,
                  inactive: newList.filter(s => s.status === "inactive").length,
                  cancelled: newList.filter(s => s.status === "cancelled").length,
                  pastDue: newList.filter(s => s.status === "past_due").length,
                  archived: newList.filter(s => s.status === "archived").length,
                };
                setStats(newStats);
                toast.success("Subscriber deleted");
              } catch (err) {
                console.error("Failed to delete subscriber:", err);
                toast.error("Failed to delete subscriber");
              }
            }}
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  ));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Manage subscribers and view subscription metrics
          </p>
        </div>
        <Button
          onClick={() => navigate("/admin/affiliates")}
          variant="outline"
          className="gap-2"
          data-testid="view-affiliates-btn"
        >
          Manage Affiliates
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscribers</CardTitle>
            <CreditCard className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{stats.active}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactive</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{stats.inactive}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Past Due / Cancelled</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {stats.pastDue + stats.cancelled}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Archived</CardTitle>
            <Archive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">{stats.archived}</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleExportUsers} className="gap-2" data-testid="export-users-btn">
          <Download className="h-4 w-4" /> Export Users CSV
        </Button>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary" size="sm">
              <Plus className="h-4 w-4 mr-2" /> Create Subscriber
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Subscriber</DialogTitle>
              <DialogDescription>Manually add a subscriber record</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSubmit} className="space-y-4 mt-2">
              <div>
                <Label>Name</Label>
                <Input value={newSubscriber.name} onChange={(e) => setNewSubscriber({...newSubscriber, name: e.target.value})} required />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={newSubscriber.email} onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})} required />
              </div>
              <div>
                <Label>Password (optional)</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} value={newSubscriber.password} onChange={(e) => setNewSubscriber({...newSubscriber, password: e.target.value})} placeholder="Leave blank to auto-generate" className="pr-10" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Plan Amount (PHP)</Label>
                <Input type="number" value={newSubscriber.plan_amount} onChange={(e) => setNewSubscriber({...newSubscriber, plan_amount: Number(e.target.value)})} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={newSubscriber.status} onValueChange={(v) => setNewSubscriber({...newSubscriber, status: v})}>
                  <SelectTrigger>
                    <SelectValue>{newSubscriber.status}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">active</SelectItem>
                    <SelectItem value="inactive">inactive</SelectItem>
                    <SelectItem value="archived">archived</SelectItem>
                    <SelectItem value="cancelled">cancelled</SelectItem>
                    <SelectItem value="past_due">past_due</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={newSubscriber.role} onValueChange={(v) => setNewSubscriber({...newSubscriber, role: v})}>
                  <SelectTrigger>
                    <SelectValue>{newSubscriber.role}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">user</SelectItem>
                    <SelectItem value="admin">admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>PayPal Subscription ID (optional)</Label>
                <Input value={newSubscriber.paypal_subscription_id} onChange={(e) => setNewSubscriber({...newSubscriber, paypal_subscription_id: e.target.value})} />
              </div>
              <div>
                <Label>Paid Until (YYYY-MM-DD, optional)</Label>
                <Input value={newSubscriber.paid_until} onChange={(e) => setNewSubscriber({...newSubscriber, paid_until: e.target.value})} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Login Details Dialog */}
        <Dialog open={!!loginDetails} onOpenChange={(open) => { if (!open) setLoginDetails(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Subscriber Login Details</DialogTitle>
              <DialogDescription>Share these credentials with the subscriber.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2 rounded-md border p-4 bg-muted text-sm font-mono">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Name:</span>
                <span className="font-semibold">{loginDetails?.name}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-semibold">{loginDetails?.email}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Password:</span>
                <span className="font-semibold">{loginDetails?.password}</span>
              </div>
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => {
                navigator.clipboard.writeText(`Name: ${loginDetails?.name}\nEmail: ${loginDetails?.email}\nPassword: ${loginDetails?.password}`)
                  .then(() => toast.success("Copied to clipboard"))
                  .catch(() => toast.error("Could not copy - please select and copy manually"));
              }}>
                <Copy className="h-4 w-4 mr-2" /> Copy
              </Button>
              <Button variant="outline" onClick={() => {
                const win = window.open("", "_blank", "width=400,height=300");
                if (win) {
                  const pre = win.document.createElement("pre");
                  pre.style.cssText = "font-family:monospace;padding:24px;font-size:14px";
                  pre.textContent = `Subscriber Login Details\n\nName:     ${loginDetails?.name}\nEmail:    ${loginDetails?.email}\nPassword: ${loginDetails?.password}`;
                  win.document.body.appendChild(pre);
                  win.document.close();
                  win.focus();
                  win.print();
                }
              }}>
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
              <Button onClick={() => setLoginDetails(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      

      {/* Active Subscribers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Active Subscribers</CardTitle>
          <CardDescription>
            Operational subscription records (non-archived). Scroll horizontally to see all columns — the name column and header row stay frozen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeSubscribers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No active subscribers found
            </div>
          ) : (
            <Table containerClassName="max-h-[600px]">
              <TableHeader className="sticky top-0 z-20 bg-background">
                <TableRow>
                  <TableHead className="sticky left-0 z-30 bg-background border-r whitespace-nowrap">User</TableHead>
                  <TableHead className="whitespace-nowrap">Business</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                  <TableHead className="whitespace-nowrap">Risk</TableHead>
                  <TableHead className="whitespace-nowrap">Signed Up</TableHead>
                  <TableHead className="whitespace-nowrap">Last Activity</TableHead>
                  <TableHead className="whitespace-nowrap">Activity Count</TableHead>
                  <TableHead className="whitespace-nowrap">Activated</TableHead>
                  <TableHead className="whitespace-nowrap">Logged In</TableHead>
                  <TableHead className="whitespace-nowrap">Created Client</TableHead>
                  <TableHead className="whitespace-nowrap">Created Time Entry</TableHead>
                  <TableHead className="whitespace-nowrap">Created Invoice</TableHead>
                  <TableHead className="whitespace-nowrap">Used Financials</TableHead>
                  <TableHead className="whitespace-nowrap">Used Budget Planner</TableHead>
                  <TableHead className="whitespace-nowrap">Drop-Off Stage</TableHead>
                  <TableHead className="whitespace-nowrap">PayPal Subscription ID</TableHead>
                  <TableHead className="whitespace-nowrap">Paid Until</TableHead>
                  <TableHead className="whitespace-nowrap">Last Payment</TableHead>
                  <TableHead className="whitespace-nowrap">Monthly Amount</TableHead>
                  <TableHead className="whitespace-nowrap">Source</TableHead>
                  <TableHead className="whitespace-nowrap">Role</TableHead>
                  <TableHead className="whitespace-nowrap">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderSubscriberRows(activeSubscribers)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Archived Subscribers Table */}
      {archivedSubscribers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-muted-foreground" />
              Archived Subscribers
            </CardTitle>
            <CardDescription>
              Historical records that have been archived. Edit an archived record to restore it to the active list.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table containerClassName="max-h-[400px]">
              <TableHeader className="sticky top-0 z-20 bg-background">
                <TableRow>
                  <TableHead className="sticky left-0 z-30 bg-background border-r whitespace-nowrap">User</TableHead>
                  <TableHead className="whitespace-nowrap">Business</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                  <TableHead className="whitespace-nowrap">Risk</TableHead>
                  <TableHead className="whitespace-nowrap">Signed Up</TableHead>
                  <TableHead className="whitespace-nowrap">Last Activity</TableHead>
                  <TableHead className="whitespace-nowrap">Activity Count</TableHead>
                  <TableHead className="whitespace-nowrap">Activated</TableHead>
                  <TableHead className="whitespace-nowrap">Logged In</TableHead>
                  <TableHead className="whitespace-nowrap">Created Client</TableHead>
                  <TableHead className="whitespace-nowrap">Created Time Entry</TableHead>
                  <TableHead className="whitespace-nowrap">Created Invoice</TableHead>
                  <TableHead className="whitespace-nowrap">Used Financials</TableHead>
                  <TableHead className="whitespace-nowrap">Used Budget Planner</TableHead>
                  <TableHead className="whitespace-nowrap">Drop-Off Stage</TableHead>
                  <TableHead className="whitespace-nowrap">PayPal Subscription ID</TableHead>
                  <TableHead className="whitespace-nowrap">Paid Until</TableHead>
                  <TableHead className="whitespace-nowrap">Last Payment</TableHead>
                  <TableHead className="whitespace-nowrap">Monthly Amount</TableHead>
                  <TableHead className="whitespace-nowrap">Source</TableHead>
                  <TableHead className="whitespace-nowrap">Role</TableHead>
                  <TableHead className="whitespace-nowrap">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderSubscriberRows(archivedSubscribers)}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Trial Reminders */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Trial Reminders
          </CardTitle>
          <CardDescription>
            Send in-app reminders to users about free trial expiry and subscription continuation at ₱699/month.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col gap-1">
              <Button
                variant="outline"
                onClick={handleSendTrialReminders}
                disabled={sendingReminder !== null}
                className="gap-2"
              >
                <Clock className="h-4 w-4" />
                {sendingReminder === "trial" ? "Sending…" : "Send Trial-Ending Reminders"}
              </Button>
              {trialPreview !== null && (
                <span className="text-xs text-muted-foreground pl-1">
                  {trialPreview.eligible_count} user(s) eligible right now
                </span>
              )}
            </div>
            <Button variant="ghost" onClick={handleViewReminderLogs} className="gap-2">
              View Send Logs
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Reminders are idempotent — a duplicate will not be sent to the same user on the same day.
            Default windows: 7, 3, and 1 day(s) before expiry.
          </p>
        </CardContent>
      </Card>

      {/* Reminder Logs Dialog */}
      <Dialog open={reminderLogsOpen} onOpenChange={setReminderLogsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reminder Send Logs</DialogTitle>
            <DialogDescription>Recent in-app notifications sent to subscribers</DialogDescription>
          </DialogHeader>
          {reminderLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No reminder logs found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Days Remaining</TableHead>
                  <TableHead>Sent Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reminderLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Badge variant="outline" className="text-blue-500">
                        Trial
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{log.workspace_id}</TableCell>
                    <TableCell>{log.days_remaining}</TableCell>
                    <TableCell>{log.sent_date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderLogsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement Banner Management */}
      <Card data-testid="announcements-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5" />
                In-App Announcements
              </CardTitle>
              <CardDescription>
                Manage banners shown to all users (trial reminders, maintenance notices, etc.)
              </CardDescription>
            </div>
            <Button size="sm" onClick={openNewAnnouncement} className="gap-2" data-testid="new-announcement-btn">
              <Plus className="h-4 w-4" /> New Announcement
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">No announcements yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Dismissible</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {announcements.map((ann) => (
                    <TableRow key={ann.id}>
                      <TableCell className="font-medium max-w-[180px] truncate" title={ann.title}>{ann.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          ann.type === "critical" ? "text-red-500 border-red-500/30" :
                          ann.type === "warning" ? "text-yellow-500 border-yellow-500/30" :
                          "text-blue-500 border-blue-500/30"
                        }>
                          {ann.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={ann.active ? "text-green-500 border-green-500/30" : "text-gray-400 border-gray-400/30"}>
                          {ann.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{ann.start_datetime?.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-xs">{ann.end_datetime ? ann.end_datetime.slice(0, 16).replace("T", " ") : "—"}</TableCell>
                      <TableCell>{ann.dismissible ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditAnnouncement(ann)} title="Edit" data-testid={`edit-announcement-${ann.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleToggleAnnouncement(ann)} title={ann.active ? "Deactivate" : "Activate"}>
                            <Archive className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteAnnouncement(ann)} title="Delete" className="text-destructive hover:text-destructive">
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Announcement Create/Edit Dialog */}
      <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAnnouncement ? "Edit Announcement" : "New Announcement"}</DialogTitle>
            <DialogDescription>
              Active announcements are shown as banners to all logged-in users.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAnnouncementSubmit} className="space-y-4 mt-2">
            <div>
              <Label>Title</Label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                placeholder="e.g. Your trial is ending soon"
                required
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                placeholder="Enter announcement text…"
                rows={3}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={announcementForm.type} onValueChange={(v) => setAnnouncementForm({ ...announcementForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="motivational">Motivational</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Audience</Label>
                <Select value={announcementForm.audience} onValueChange={(v) => setAnnouncementForm({ ...announcementForm, audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date/Time</Label>
                <Input
                  type="datetime-local"
                  value={announcementForm.start_datetime}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, start_datetime: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>End Date/Time (optional)</Label>
                <Input
                  type="datetime-local"
                  value={announcementForm.end_datetime}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, end_datetime: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="dismissible"
                  checked={announcementForm.dismissible}
                  onCheckedChange={(v) => setAnnouncementForm({ ...announcementForm, dismissible: v })}
                />
                <Label htmlFor="dismissible">Dismissible by users</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="ann-active"
                  checked={announcementForm.active}
                  onCheckedChange={(v) => setAnnouncementForm({ ...announcementForm, active: v })}
                />
                <Label htmlFor="ann-active">Active now</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAnnouncementDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingAnnouncement ? "Save Changes" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <UserActivityModal
        open={activityModalOpen}
        onClose={() => setActivityModalOpen(false)}
        subscriber={activitySubscriber}
      />

      {/* Edit Subscription Dialog (admin-created only) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
            <DialogDescription>
              Update subscription details for {editingSubscriber?.user_name || editingSubscriber?.user_email || "subscriber"}.
              Only admin-created subscriptions can be modified.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan Amount (₱)</Label>
              <Input
                type="number"
                min={0}
                value={editForm.plan_amount}
                onChange={(e) => setEditForm({ ...editForm, plan_amount: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div>
              <Label>Paid Until (YYYY-MM-DD)</Label>
              <Input
                placeholder="e.g. 2030-12-31"
                value={editForm.paid_until}
                onChange={(e) => setEditForm({ ...editForm, paid_until: e.target.value })}
              />
            </div>
            <div>
              <Label>Currency</Label>
              <Select
                value={editForm.currency}
                onValueChange={(v) => setEditForm({ ...editForm, currency: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PHP">PHP</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>PayPal Subscription ID (optional)</Label>
              <Input
                placeholder="I-XXXXXXXXXXXX"
                value={editForm.paypal_subscription_id}
                onChange={(e) => setEditForm({ ...editForm, paypal_subscription_id: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
